import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface AppSettings {
  theme: 'light' | 'dark' | 'auto'
  language: string
  sidebarCollapsed: boolean
  notifications: boolean
}

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  timestamp: Date
  read: boolean
}

export const useAppStore = defineStore('app', () => {
  // Estado reativo
  const settings = ref<AppSettings>({
    theme: 'light',
    language: 'pt-BR',
    sidebarCollapsed: false,
    notifications: true
  })

  const notifications = ref<Notification[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters computados
  const unreadNotifications = computed(() => 
    notifications.value.filter(n => !n.read)
  )

  const unreadCount = computed(() => unreadNotifications.value.length)

  const currentTheme = computed(() => settings.value.theme)

  // Actions
  const updateSettings = (newSettings: Partial<AppSettings>) => {
    settings.value = { ...settings.value, ...newSettings }
    localStorage.setItem('app-settings', JSON.stringify(settings.value))
  }

  const toggleSidebar = () => {
    settings.value.sidebarCollapsed = !settings.value.sidebarCollapsed
    localStorage.setItem('app-settings', JSON.stringify(settings.value))
  }

  const setTheme = (theme: AppSettings['theme']) => {
    settings.value.theme = theme
    localStorage.setItem('app-settings', JSON.stringify(settings.value))
    
    // Sincronizar com a chave kt-theme usada pelo script do index.html
    let themeToApply = theme
    if (theme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      themeToApply = prefersDark ? 'dark' : 'light'
    }
    
    // Salvar no localStorage com a chave esperada pelo KTUI
    localStorage.setItem('kt-theme', themeToApply)
    
    // Aplicar tema usando data-theme conforme esperado pelo CSS KTUI
    document.documentElement.removeAttribute('data-theme')
    if (themeToApply === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark')
    }
    
    // Também remover classes antigas se existirem
    document.documentElement.classList.remove('light', 'dark')
    document.documentElement.classList.add(themeToApply)
  }

  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      read: false
    }
    
    notifications.value.unshift(newNotification)
    
    // Limitar a 50 notificações
    if (notifications.value.length > 50) {
      notifications.value = notifications.value.slice(0, 50)
    }
  }

  const markNotificationAsRead = (id: string) => {
    const notification = notifications.value.find(n => n.id === id)
    if (notification) {
      notification.read = true
    }
  }

  const markAllNotificationsAsRead = () => {
    notifications.value.forEach(notification => {
      notification.read = true
    })
  }

  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  const clearNotifications = () => {
    notifications.value = []
  }

  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const loadSettings = () => {
    // Primeiro, verificar se há configurações salvas
    const storedSettings = localStorage.getItem('app-settings')
    if (storedSettings) {
      try {
        const parsed = JSON.parse(storedSettings)
        settings.value = { ...settings.value, ...parsed }
      } catch (error) {
        console.error('Erro ao carregar configurações:', error)
      }
    }
    
    // Verificar se há tema salvo na chave kt-theme (usado pelo KTUI)
    const storedTheme = localStorage.getItem('kt-theme')
    if (storedTheme && (storedTheme === 'light' || storedTheme === 'dark')) {
      settings.value.theme = storedTheme
    }
    
    // Aplicar o tema
    setTheme(settings.value.theme)
  }

  // Detectar tema do sistema
  const detectSystemTheme = () => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  // Inicialização
  const init = () => {
    // Carregar configurações primeiro
    loadSettings()
    
    // Escutar mudanças na preferência do sistema quando for modo auto
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (settings.value.theme === 'auto') {
        setTheme('auto') // Reaplica o tema automático
      }
    })
    
    // Adicionar algumas notificações de exemplo  
    addNotification({
      type: 'info',
      title: 'Bem-vindo!',
      message: 'Bem-vindo ao FlowPilot. Explore os recursos disponíveis.'
    })
  }

  return {
    // Estado
    settings,
    notifications,
    isLoading,
    error,
    // Getters
    unreadNotifications,
    unreadCount,
    currentTheme,
    // Actions
    updateSettings,
    toggleSidebar,
    setTheme,
    addNotification,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    removeNotification,
    clearNotifications,
    setLoading,
    setError,
    loadSettings,
    detectSystemTheme,
    init
  }
})